<template>
  <div :class="[$style.config, $style.wrapper]" data-products-grid-v="3.1.2">
    <div :class="$style.filterContainer">
      <div :class="$style.filterInner">
        <!-- filter button list -->
        <div :class="$style.brandFiltersOverflow">
          <div :class="$style.brandFilters">
            <BaseButton
              v-for="filter in data?.fields?.filterTags"
              :key="filter?.sys?.id"
              :presentation="
                activeFilter === filter?.fields?.value ? 'default' : 'clear'
              "
              :class="$style.brandFilter"
              @click="handleFilterClick(filter)"
            >
              {{ filter?.fields?.label }}
            </BaseButton>
          </div>
        </div>

        <div :class="$style.filtersContainer">
          <!-- search filter -->
          <SearchField
            :class="$style.search"
            @request-query="handleSearch"
            @clear-query="handleSearch"
          />
        </div>
      </div>
    </div>

    <div :class="$style.inner">
      <div
        v-if="
          !products.length &&
          (evergreenProductsStatus === 'pending' ||
            !hasCurrentEvergreenProducts ||
            accessibleEntryIdsStatus === 'pending' ||
            productsResponseStatus === 'pending' ||
            !hasCurrentAccessibleEntryIds)
        "
        :class="$style.loaderDots"
      >
        <LoaderDots />
      </div>

      <template v-else-if="products.length > 0">
        <div :class="$style.productContainer">
          <ProductSetCard
            v-for="product in products"
            :key="product?.sys?.id"
            :data="product"
          />
        </div>
      </template>

      <template
        v-else-if="
          !products.length &&
          productsResponseStatus === 'success' &&
          hasCurrentAccessibleEntryIds
        "
      >
        <div :class="$style.noResults">
          {{ localizedStrings['no-results'] || 'No Results' }}
        </div>
      </template>

      <CtaWings
        v-if="
          accessibleEntryIdsStatus === 'success' &&
          productsResponseStatus === 'success' &&
          data?.fields?.cta &&
          products.length < totalProductsAvailable
        "
      >
        <CallToAction :cta="data?.fields?.cta" @click="onRequestMore" />
      </CtaWings>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { LocationQueryValue } from 'vue-router'
import type {
  TypeProductDetailsPageSkeleton,
  TypeProductDetailsPageWithoutUnresolvableLinksResponse,
  TypeProductsGridWithoutUnresolvableLinksResponse,
  TypeTagWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
const { $client, $userAccessCheck, $visibility } = useNuxtApp()
const { localizedStrings } = useLocalizedStrings()
const { highestUserLevel } = storeToRefs(useUserStore())
const { locale } = useI18n()
const route = useRoute()
const router = useRouter()

const COMPONENT = 'products-grid'
const ITEMS_PER_PAGE = 8

const props = defineProps<{
  data: TypeProductsGridWithoutUnresolvableLinksResponse
}>()

const searchQuery = ref('')
const evergreenResolvedSignature = ref('')
const evergreenProducts = useState<
  (TypeProductDetailsPageWithoutUnresolvableLinksResponse | undefined)[]
>('productsGridEvergreenProducts', () => [])
const regularProducts = useState<
  (TypeProductDetailsPageWithoutUnresolvableLinksResponse | undefined)[]
>('productsGridRegularProducts', () => [])

const products = computed(() => [
  ...evergreenProducts.value,
  ...regularProducts.value,
])

const evergreenProductIds = computed(
  () =>
    evergreenProducts.value
      ?.map?.((item) => item?.sys?.id)
      .filter((id): id is string => Boolean(id)) || [],
)
const evergreenIdsSignature = computed(() =>
  evergreenProductIds.value.join('|'),
)

const totalProductsAvailable = computed(
  () =>
    evergreenProducts.value.length +
    (accessibleEntryIds.value?.entryIds?.length || 0),
)

const initialRegularItemsPerPage = computed(() => {
  return Math.max(ITEMS_PER_PAGE - evergreenProducts.value.length, 0)
})

const isValidTypeQuery = (type: LocationQueryValue | LocationQueryValue[]) => {
  const formattedTypeQuery = type?.toString()
  const filterTagIndex =
    props.data?.fields?.filterTags?.findIndex?.(
      (tag) => tag?.fields?.value === formattedTypeQuery,
    ) || -1
  return filterTagIndex > -1
}

const activeFilter = ref(
  isValidTypeQuery(route?.query?.type) ? route?.query?.type : 'all',
)

const cookieStore = useCookieStore()

const KEY_VALUES = `${searchQuery.value}-${activeFilter.value}`
const currentRequestSignature = computed(() => {
  return [
    activeFilter.value,
    searchQuery.value,
    locale.value,
    cookieStore.languageCookie,
    cookieStore.countryCookie,
  ].join('|')
})
const hasCurrentEvergreenProducts = computed(() => {
  return evergreenResolvedSignature.value === currentRequestSignature.value
})
const hasCurrentAccessibleEntryIds = computed(() => {
  return (
    hasCurrentEvergreenProducts.value &&
    accessibleEntryIds.value?.requestSignature ===
      currentRequestSignature.value &&
    accessibleEntryIds.value?.evergreenIdsSignature ===
      evergreenIdsSignature.value
  )
})

const EVERGREEN_PRODUCTS_KEY = useDataKey(
  `${COMPONENT}-evergreen-${KEY_VALUES}`,
)

const { status: evergreenProductsStatus } = await useAsyncData(
  EVERGREEN_PRODUCTS_KEY,
  async () => {
    const requestSignature = currentRequestSignature.value
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeProductDetailsPageSkeleton>(
        {
          content_type: 'productDetailsPage',
          include: 10,
          limit: 500,
          locale: locale.value,
          order: ['-fields.releaseDate'],
          select: [
            'sys.id',
            'fields.name',
            'fields.brand',
            'fields.media',
            'fields.path',
            'fields.releaseDate',
            'fields.visibility',
            'fields.accessLevel',
            'fields.evergreen',
          ],
          'fields.evergreen': true,
          'fields.brand.sys.contentType.sys.id': 'tag',
          'fields.brand.fields.value[match]':
            activeFilter.value !== 'all' ? [activeFilter.value] : '',
          'fields.name[match]': searchQuery.value,
        },
      )

    const accessibleEntries =
      response?.items?.filter?.((item) => {
        const hasAccess = $userAccessCheck(
          item.fields?.accessLevel,
          highestUserLevel.value,
        )
        const isVisible = $visibility(item?.fields?.visibility)
        return hasAccess && isVisible
      }) || []

    return {
      products: accessibleEntries,
      requestSignature,
      fetchedAt: useUnixTimestamp(),
    }
  },
  {
    transform(input) {
      if (input.requestSignature !== currentRequestSignature.value) {
        return input
      }

      evergreenProducts.value = input.products
      evergreenResolvedSignature.value = input.requestSignature
      return input
    },
    watch: [
      activeFilter,
      searchQuery,
      () => cookieStore.languageCookie,
      () => cookieStore.countryCookie,
    ],
  },
)

// Fetch accessible product IDs
const ACCESSIBLE_PRODUCT_IDS_KEY = useDataKey(
  `${COMPONENT}-accessible-ids-${KEY_VALUES}`,
)

const { status: accessibleEntryIdsStatus, data: accessibleEntryIds } =
  await useAsyncData(
    ACCESSIBLE_PRODUCT_IDS_KEY,
    async () => {
      if (!hasCurrentEvergreenProducts.value) {
        return {
          entryIds: [],
          requestSignature: '',
          evergreenIdsSignature: '',
          skipUpdate: true,
          fetchedAt: useUnixTimestamp(),
        }
      }

      const requestSignature = currentRequestSignature.value
      const requestEvergreenIdsSignature = evergreenIdsSignature.value
      // Get IDs and visibility of 500 latest products
      const response =
        await $client.withoutUnresolvableLinks.getEntries<TypeProductDetailsPageSkeleton>(
          {
            content_type: 'productDetailsPage',
            include: 10,
            limit: 500,
            locale: locale.value,
            order: ['-fields.releaseDate'],
            select: ['sys.id', 'fields.visibility', 'fields.accessLevel'],
            // Brand filter
            'fields.brand.sys.contentType.sys.id': 'tag',
            'fields.brand.fields.value[match]':
              activeFilter.value !== 'all' ? [activeFilter.value] : '',
            // Search
            'fields.name[match]': searchQuery.value,
          },
        )

      // Create array of IDs that are visible to current location/locale
      const accessibleEntries = response?.items?.filter?.((item) => {
        const hasAccess = $userAccessCheck(
          item.fields?.accessLevel,
          highestUserLevel.value,
        )
        const isVisible = $visibility(item?.fields?.visibility)
        const isEvergreen = evergreenProductIds.value.includes(
          item?.sys?.id || '',
        )
        return hasAccess && isVisible && !isEvergreen
      })

      const entryIds = accessibleEntries?.map?.((item) => item?.sys?.id)

      return {
        entryIds,
        requestSignature,
        evergreenIdsSignature: requestEvergreenIdsSignature,
        fetchedAt: useUnixTimestamp(),
      }
    },
    {
      watch: [
        evergreenProductIds,
        activeFilter,
        searchQuery,
        () => cookieStore.languageCookie,
        () => cookieStore.countryCookie,
      ],
    },
  )

const loadMoreCount = ref(0)
const regularProductsOffset = computed(() => {
  if (loadMoreCount.value === 0) return 0

  return (
    initialRegularItemsPerPage.value +
    (loadMoreCount.value - 1) * ITEMS_PER_PAGE
  )
})

// Fetch paginated event entries
const PAGE_DATA_KEY = useDataKey(
  `${COMPONENT}-page-${loadMoreCount.value}-${KEY_VALUES}`,
)

const { status: productsResponseStatus } = await useAsyncData(
  PAGE_DATA_KEY,
  async () => {
    if (!hasCurrentAccessibleEntryIds.value) {
      return { items: [], skipUpdate: true }
    }

    const accessibleIds = accessibleEntryIds?.value?.entryIds || []
    const itemsToFetch =
      loadMoreCount.value === 0
        ? initialRegularItemsPerPage.value
        : ITEMS_PER_PAGE
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeProductDetailsPageSkeleton>(
        {
          content_type: 'productDetailsPage',
          include: 10,
          limit: itemsToFetch,
          locale: locale.value,
          order: ['-fields.releaseDate'],
          'sys.id[in]': [
            ...accessibleIds.slice(
              regularProductsOffset.value,
              regularProductsOffset.value + itemsToFetch,
            ),
          ],
          select: [
            'sys.id',
            'fields.name',
            'fields.brand',
            'fields.media',
            'fields.path',
            'fields.evergreen',
            'fields.releaseDate',
          ],
        },
      )

    return response
  },
  {
    transform(input) {
      if ('skipUpdate' in input && input.skipUpdate) {
        return {
          products: input,
          fetchedAt: useUnixTimestamp(),
        }
      }

      regularProducts.value =
        loadMoreCount.value === 0
          ? input.items
          : [
              ...regularProducts.value.slice(0, regularProductsOffset.value),
              ...input.items,
            ]
      return {
        products: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    watch: [accessibleEntryIds, loadMoreCount],
  },
)

watch(
  [() => activeFilter.value, () => searchQuery.value],
  ([newFilter, newQuery], [oldFilter, oldQuery]) => {
    if (newFilter !== oldFilter || newQuery !== oldQuery) {
      loadMoreCount.value = 0
      evergreenResolvedSignature.value = ''
      evergreenProducts.value = []
      regularProducts.value = []
    }
  },
  { immediate: false },
)

watch(
  evergreenProductIds,
  (newIds, oldIds) => {
    if (newIds.join(',') !== oldIds.join(',')) {
      loadMoreCount.value = 0
      regularProducts.value = []
    }
  },
  { immediate: false },
)

watch(
  () => route.query,
  () => {
    const type = route?.query?.type || ''
    activeFilter.value = isValidTypeQuery(type) ? type : 'all'
  },
  { immediate: false },
)

// We use `loadMoreCount` instead of `currentPage` because pagination is driven by
// how many items have already been loaded—not the actual page number.
//
// This avoids a double-fetch issue:
// - The paginated `useAsyncData` watches both `accessibleEntryIds` and the old `currentPage`
// - When filters change (e.g. new search), we used to reset both `products` and `currentPage`
// - That caused two reactive triggers—`accessibleEntryIds` first, then `currentPage`
// - Result: the same slice would be fetched twice in a row
//
// With `loadMoreCount`, we only increment on "Load More" clicks.
// Since the offset is based on `products.value.length`, we don’t need an actual page number,
// and we no longer have to reset any pagination state when filters change.
//
// See:
//   accessibleIds.slice(products.length, products.length + ITEMS_PER_PAGE)
const onRequestMore = () => (loadMoreCount.value += 1)

const handleSearch = (val: string) => (searchQuery.value = val)

const handleFilterClick = (
  tag: TypeTagWithoutUnresolvableLinksResponse | undefined,
) => {
  activeFilter.value = tag?.fields?.value || ''

  // update query type
  router.push({ query: { ...route.query, type: activeFilter.value } })

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'filter_applied',
    eventParams: {
      filter_name: tag?.fields?.value,
    },
  })
}
</script>

<style lang="scss" module>
.config {
  --max-width: 1310px;
  --padding-spacing: 20px;

  --wrapper-padding-bottom: 62px;

  @include media('sm') {
    --wrapper-padding-bottom: 85px;
  }
}

.wrapper {
  padding-bottom: var(--wrapper-padding-bottom);
}

.inner {
  min-height: 300px;
  max-width: calc(var(--max-width) + var(--padding-spacing));
  margin: 0 auto;
  padding: 18px var(--padding-spacing) 0;

  @include media('md') {
    padding: 40px var(--padding-spacing) 0;
  }
}

.filterContainer {
  background-color: var(--white);
  box-shadow: 0 1px 3px var(--gray-4);
  padding-top: 8px;
}

.filterInner {
  display: flex;
  flex-wrap: wrap;
  max-width: calc(var(--max-width) + var(--padding-spacing));
  margin: 0 auto;
  padding: 0 var(--padding-spacing);

  @include media('md') {
    align-items: center;
    justify-content: space-between;
  }
}

.brandFiltersOverflow {
  margin-right: calc(-1 * var(--padding-spacing));
  overflow: hidden;
}

.brandFilters {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  gap: 10px;
  justify-content: flex-start;
  margin-bottom: 3px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.brandFilter {
  --button-min-width: 100px;
  flex: 0 0 auto;
  white-space: nowrap;
}

.filtersContainer {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 11px;
  width: 100%;

  @include media('xs') {
    margin-left: auto;
    width: auto;
  }
}

.search {
  min-width: 223px;
  width: 100%;

  @include media('xs') {
    width: auto;
  }
}

.productContainer {
  padding-bottom: 26px;
  margin: 0 auto;
  display: grid;
  gap: 17px;

  @include media('xs') {
    grid-template-columns: 1fr 1fr;
  }

  @include media('sm') {
    grid-template-columns: repeat(3, 1fr);
    padding-bottom: 45px;
  }

  @include media('md') {
    gap: 25px;
    grid-template-columns: repeat(4, 1fr);
  }
}

.loaderDots {
  display: grid;
  place-items: center;

  margin: auto;
  width: max-content;
  height: 300px;
}

.noResults {
  display: grid;
  place-items: center;
  min-height: 300px;
  text-align: center;
}
</style>
